// Figure order (DESIGN_SPEC §11.4.3–11.4.4, RESTYLE §4): the document's journal rows, FIG numbering in DOM order,
// the figure-index entries and the sections that were absent. Each panel module exports a default component
// (FigProps) and meta(ctx).
//   planDocument(ctx, { only? }) → { rows, figs, absent }
//   absentLine(absent, ctx)      → one short phrase per absent section, joined with " · ", for the line under the last
//                                  figure; while the event stream has delivered nothing (streamPending) the event-derived
//                                  sections are "not loaded", never "no … recorded"
//   streamPending(stream, job)   → a dispatched run whose stream has not delivered (yet): nothing may be claimed absent
import type { ComponentType } from "react";
import type { FigModule, FigProps, JobStream, ReportCtx } from "@/lib/derive/types";
import * as ArtifactsFig from "./panels/ArtifactsFig";
import * as AttackFig from "./panels/AttackFig";
import * as BehaviorFig from "./panels/BehaviorFig";
import * as ConfigFig from "./panels/ConfigFig";
import * as EventStreamFig from "./panels/EventStreamFig";
import * as IndicatorsFig from "./panels/IndicatorsFig";
import * as NetworkFig from "./panels/NetworkFig";
import * as ProcessTreeFig from "./panels/ProcessTreeFig";
import * as SessionFig from "./panels/SessionFig";
import * as StaticPeFig from "./panels/StaticPeFig";
import * as ThirdPartyFig from "./panels/ThirdPartyFig";

export type SectionId = "behavior" | "attack" | "thirdparty" | "config" | "session" | "processes" | "network" | "events" | "indicators" | "static" | "artifacts";

const SECTIONS: Record<SectionId, { module: FigModule; label: string }> = {
  behavior: { module: BehaviorFig, label: "Behavior" },
  attack: { module: AttackFig, label: "ATT&CK" },
  thirdparty: { module: ThirdPartyFig, label: "Third-party" },
  config: { module: ConfigFig, label: "Config" },
  session: { module: SessionFig, label: "Session" },
  processes: { module: ProcessTreeFig, label: "Processes" },
  network: { module: NetworkFig, label: "Network" },
  events: { module: EventStreamFig, label: "Events" },
  indicators: { module: IndicatorsFig, label: "Indicators" },
  static: { module: StaticPeFig, label: "Static PE" },
  artifacts: { module: ArtifactsFig, label: "Artifacts" },
};

/** Journal rows: a pair shares a row (the second panel is the s4 side cell); a lone panel spans the row. */
const DOCUMENT_ROWS: SectionId[][] = [["behavior", "attack"], ["thirdparty"], ["config"], ["session"], ["processes"], ["network"], ["events"], ["indicators"], ["static"], ["artifacts"]];

export type PlannedFig = {
  section: SectionId;
  /** meta id (falls back to the section id) */
  id: string;
  label: string;
  count?: number;
  no: number;
  Component: ComponentType<FigProps>;
};

export type DocumentPlan = { rows: PlannedFig[][]; figs: PlannedFig[]; absent: SectionId[] };

export function planDocument(ctx: ReportCtx, opts: { only?: readonly SectionId[] } = {}): DocumentPlan {
  const rows: PlannedFig[][] = [];
  const figs: PlannedFig[] = [];
  const absent: SectionId[] = [];
  let no = 0;
  for (const row of DOCUMENT_ROWS) {
    const planned: PlannedFig[] = [];
    for (const section of row) {
      if (opts.only && !opts.only.includes(section)) continue;
      const { module, label } = SECTIONS[section];
      // a meta() that throws still gets its figure: the panel's own boundary reports the failure
      let meta: ReturnType<FigModule["meta"]> | null = null;
      try {
        meta = module.meta(ctx);
      } catch {
        meta = null;
      }
      if (meta?.empty) {
        absent.push(section);
        continue;
      }
      const fig: PlannedFig = { section, id: meta?.id ?? section, label: meta?.label ?? label, count: meta?.count, no: ++no, Component: module.default };
      planned.push(fig);
      figs.push(fig);
    }
    if (planned.length) rows.push(planned);
  }
  return { rows, figs, absent };
}

export function streamPending(stream: Pick<JobStream, "ended" | "snap">, job: Pick<ReportCtx["job"], "node_id">): boolean {
  return !!job.node_id && !stream.ended && stream.snap.events.length === 0;
}

const EVENT_SECTIONS: readonly SectionId[] = ["processes", "network", "events"];

export function absentLine(absent: readonly SectionId[], ctx: ReportCtx): string {
  const out: string[] = [];
  const isUrl = ctx.job.kind === "url";
  const has = (s: SectionId) => absent.includes(s);
  const pending = streamPending(ctx.stream, ctx.job);
  if (has("behavior")) out.push(ctx.stream.verdict ? "no behavior rule fired" : "no verdict");
  const tagged = (ctx.behaviorReport?.tags ?? ctx.stream.verdict?.tags ?? []).length > 0;
  if (has("attack") && !has("behavior") && tagged) out.push("no ATT&CK techniques");
  if (has("session")) out.push("no replay or screenshots");
  if (pending) {
    const w = EVENT_SECTIONS.filter(has).map((s) => SECTIONS[s].label.toLowerCase());
    const list = w.length > 1 ? `${w.slice(0, -1).join(", ")} and ${w[w.length - 1]}` : (w[0] ?? "");
    if (list) out.push(`${list} not loaded`);
  } else {
    if (has("processes")) out.push("no process activity");
    if (has("network")) out.push("no network traffic");
    if (has("events")) out.push("no events");
  }
  if (has("indicators")) out.push("no indicators");
  if (has("static")) out.push(isUrl ? "no static report (URL)" : "no static report (not a PE)");
  if (has("artifacts")) out.push("no stored files");
  return out.join(" · ");
}
